import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

export default function ProductCard({ product, index = 0, onQuickView }) {
  const { add } = useCart();

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3), ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-sand bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-forest/10"
    >
      <Link to={`/product/${product.slug}`} data-testid={`product-link-${product.slug}`} className="relative block aspect-square overflow-hidden">
        <div
          className="absolute inset-0 opacity-70 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: `radial-gradient(85% 75% at 50% 30%, ${product.accent}26 0%, transparent 70%)` }}
        />
        <img
          src={product.image}
          alt={`${product.name} packaging`}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover p-4 transition-transform duration-500 ease-out group-hover:scale-[1.06] group-hover:-rotate-1"
        />
        <span
          className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] backdrop-blur-sm"
          style={{ color: product.accent }}
        >
          {product.mascot}
        </span>
        {onQuickView && (
          <button
            data-testid={`quick-view-button-${product.slug}`}
            onClick={(e) => { e.preventDefault(); onQuickView(product); }}
            className="absolute inset-x-4 bottom-3 translate-y-2 rounded-full border border-sand bg-white/95 py-2 text-xs font-bold uppercase tracking-wider text-forest opacity-0 backdrop-blur-sm transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
          >
            Quick view
          </button>
        )}
      </Link>

      <div className="flex flex-1 flex-col gap-1 p-4 pt-2">
        <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: product.accent }}>
          {product.tagline}
        </p>
        <Link to={`/product/${product.slug}`} className="font-display text-lg font-extrabold leading-tight text-forest hover:underline">
          {product.name}
        </Link>
        <p className="text-xs leading-relaxed text-ink/60">{product.benefit}</p>

        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-2 gap-y-2 pt-3">
          <p className="whitespace-nowrap font-display text-base font-extrabold text-forest sm:text-lg">
            {inr(product.price)}
            <span className="ml-1 font-mono text-[10px] font-medium uppercase tracking-wider text-ink/40">{product.pack}</span>
          </p>
          <button
            data-testid={`quick-add-${product.slug}`}
            onClick={() => add(product)}
            aria-label={`Add ${product.name} to cart`}
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-forest px-3 text-xs font-extrabold uppercase tracking-wide text-white shadow-sm transition-all duration-200 hover:bg-leaf hover:shadow-lg active:scale-95 sm:h-10 sm:px-4"
          >
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </div>
    </motion.article>
  );
}
